import { observer } from "mobx-react";
import "./rma-list-filters-drawer.scss";
import { Drawer } from "../../../../../components/drawer/drawer";
import DefaultDrawerLayout from "../../../../../components/drawer/layouts/default-drawer-layout";
import {
	SelectDropdownGrouped
} from "../../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import rmaPagedListStore from "../../stores/rma-paged-list-store";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { Button } from "../../../../../components/inputs/button/button";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import FontHeading5 from "../../../../../components/fonts/font-heading5/font-heading5";
import IconFunnel from "../../../../../components/icons/icon-funnel/icon-funnel";
import { FormField } from "../../../../../components/form-field/form-field";
import { SingleValue } from "react-select";
import { PAYMENT_STATUS_TYPE, RESTOCK_VALIDATED_TYPE } from "../../../../../services/api/rma-list-service";
import RmaListFiltersStore from "../../stores/rma-list-filters-store";
import ReservedItemExpirationDropdown from "./reserved-item-expiration-dropdown/reserved-item-expiration-dropdown";
import currentPortalStore from "../../../../../stores/current-portal-store";
import { FEATURES } from "../../../../../models/Plan";
import useValidationGroup from "../../../../../hooks/validation-group/validation-group-hook";
import DateRangePickerV2, { IDateRangeISO } from "../../../../../components/date-range-picker-v2/date-range-picker-v2";
import { parseIsoDateRange } from "../../../../../utilities/date-utilities";
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";
import {
    MANAGE_RETURNS_DESIGN_VERSION,
    trackManageReturnsListEvent,
} from "../../../utils/manage-returns-list-mixpanel";

interface IProps {
    onClose: () => void;
    rmaListFiltersStore: RmaListFiltersStore;
}

const RmaListFiltersDrawer: React.FC<IProps> = observer(({ onClose, rmaListFiltersStore }) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-returns-page';
    const vGroup = useValidationGroup();

    const onSelectResolutionId = (value: SingleValue<IOption>) => {
        if (value?.value === 0 || !value?.value) {
            rmaListFiltersStore.setSelectedResolutionId(null);
            return;
        }
        trackManageReturnsListEvent("d&_filter_resolutions", MANAGE_RETURNS_DESIGN_VERSION.V1);
        rmaListFiltersStore.setSelectedResolutionId(Number(value.value));
    };

    const onSelectReturnReasonId = (value: SingleValue<IOption>) => {
        if (value?.value === 0 || !value?.value) {
            rmaListFiltersStore.setSelectedReturnReasonId(null);
            return;
        }
        trackManageReturnsListEvent("d&_filter_return_reason", MANAGE_RETURNS_DESIGN_VERSION.V1);
        rmaListFiltersStore.setSelectedReturnReasonId(Number(value.value));
    };

    const onSelectRestockValidated = (value: SingleValue<IOption>) => {
        trackManageReturnsListEvent("d&_filter_validated", MANAGE_RETURNS_DESIGN_VERSION.V1);
        rmaListFiltersStore.setRestockValidatedType(value?.value as RESTOCK_VALIDATED_TYPE);
    };

    const onSelectPaymentStatus = (value: SingleValue<IOption>) => {
        trackManageReturnsListEvent("d&_filter_refund_payment_status", MANAGE_RETURNS_DESIGN_VERSION.V1);
        rmaListFiltersStore.setPaymentStatusType(value?.value as PAYMENT_STATUS_TYPE);
    }


    /** Handle date range selection from DateRangePickerV2 */
    const onSelectDateV2 = (isoDateRange: IDateRangeISO | null) => {
        if (!isoDateRange?.from || !isoDateRange?.to) {
            rmaListFiltersStore.setDateRange('');
            return;
        }

        /** Store ISO format: 2025-01-01T00:00:00Z/2025-12-31T23:59:59Z */
        rmaListFiltersStore.setDateRange(`${isoDateRange.from}/${isoDateRange.to}`);
    };

    return (
        <Drawer closeButton={{ onClick: onClose }}>
            <DefaultDrawerLayout
            drawerHeader={
            <div className="rma-list-drawer-title">
                <IconFunnel color="var(--color-primary-2)" />
                <FontHeading5 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                    {getTranslation('FILTERS_TITLE', scope)}
                </FontHeading5>
            </div>
        }
            drawerBody={
                <div className="rma-list-drawer-content">
                    <FormField label={getTranslation('DATE_CREATED', scope)}>
                        <DateRangePickerV2
                            onChange={onSelectDateV2}
                            value={parseIsoDateRange(rmaListFiltersStore.dateRange, true)}
                            onOpen={() => trackManageReturnsListEvent("d&_filter_date_created", MANAGE_RETURNS_DESIGN_VERSION.V1)}
                            onApply={() => trackManageReturnsListEvent("d&_apply_date_filter", MANAGE_RETURNS_DESIGN_VERSION.V1)}
                        />
                    </FormField>
                    <FormField label={getTranslation('RESOLUTIONS', scope)}>
                        <SelectDropdownGrouped
                            isLoading={rmaListFiltersStore.isLoading}
                            options={rmaListFiltersStore.resolutionsOptions}
                            value={rmaListFiltersStore.selectedResolutionValue}
                            onChange={onSelectResolutionId}
                        />
                    </FormField>
                    <FormField label={getTranslation('RETURN_REASON', scope)}>
                        <SelectDropdown
                            isLoading={rmaListFiltersStore.isLoading}
                            options={rmaListFiltersStore.returnReasonsOptions}
                            value={rmaListFiltersStore.selectedReturnReasonValue}
                            onChange={onSelectReturnReasonId}
                        />
                    </FormField>
                    <FormField label={getTranslation('VALIDATED', scope)}>
                        <SelectDropdown
                            isLoading={rmaListFiltersStore.isLoading}
                            options={rmaListFiltersStore.restockValidatedOptions}
                            value={rmaListFiltersStore.restockValidatedValue}
                            onChange={onSelectRestockValidated}
                        />
                    </FormField>
                    {currentPortalStore.isFeatureAllowed(FEATURES.RESERVE_ITEMS) && (
                        <FormField label={getTranslation('RESERVED_ITEM_EXPIRATION', scope)}>
                            <ReservedItemExpirationDropdown
                                rmaListFiltersStore={rmaListFiltersStore}
                                validationGroup={vGroup}
                            />
                        </FormField>
                    )}

	                <ResolveCapability capability={PROFILE_CAPABILITIES.RMA_FILTERS}>
		                {({ isRefundAndPaymentStatusFilterSupported }) => isRefundAndPaymentStatusFilterSupported && (
			                <FormField label={getTranslation('REFUND_PAYMENT_STATUS', scope)}>
				                <SelectDropdown
					                isLoading={rmaListFiltersStore.isLoading}
					                options={rmaListFiltersStore.paymentStatusFilterOptions}
					                value={rmaListFiltersStore.paymentStatusFilterOptions.find(option => option.value === rmaListFiltersStore.paymentStatus)}
					                onChange={onSelectPaymentStatus}
				                />
			                </FormField>
		                )}
	                </ResolveCapability>
                </div>
            }
            drawerFooter={
                <div className="rma-list-drawer-content">
                    <Button text={getTranslation('APPLY', scope)}
                            onClick={() => {
                            trackManageReturnsListEvent("d&_apply_filter", MANAGE_RETURNS_DESIGN_VERSION.V1);
                            onClose();
                            rmaPagedListStore.loadPage(1);
                        }}
                        disabled={!rmaListFiltersStore.reserveItemExpirationFilterValid || !vGroup.isValid()}
                    />
                </div>}
        />
        </Drawer>
    )
});

export default RmaListFiltersDrawer;
